
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { validateStudentFields, getSchoolDeliveryOptions } from '@/utils/helpers';

@Component<StudentComponent>({})
export default class StudentComponent extends Vue {
	@Prop({ type: Object, required: true }) student!: UserStudent;
	@Getter('getRestaurantDeliveryInfo', { namespace: 'restaurant' }) private restaurantDeliveryInfo!: SchoolDelivery;
	@Getter('getRestaurantGroupLocations', { namespace: 'restaurant' }) private groupLocations!: GroupLocation[];
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;

	private schools: (string | undefined)[] | null = [];

	/**
	 * Get the delivery info for this student's location
	 * If student has a restaurantId, find that location's delivery info
	 * Otherwise use the current restaurant's delivery info
	 *
	 * @return {SchoolDelivery}
	 */
	private get studentDeliveryInfo(): SchoolDelivery {
		// If student has a restaurantId and we're in a group, find that location's delivery info
		// TODO: Remove the restaurant.group (but keep the '.groups' one) check when locations are ready to use restaurant.groups
		if (this.student.restaurantId && (this.restaurant.group || this.restaurant.groups?.length) && this.groupLocations.length) {
			const studentLocation = this.groupLocations.find(loc => loc.app8_restaurant === this.student.restaurantId);
			if (studentLocation?.delivery) {
				return studentLocation.delivery;
			}
		}
		// Fall back to current restaurant's delivery info
		return this.restaurantDeliveryInfo;
	}

	/**
	 * This getter fetches the school delivery information.
	 * It is used to populate the school's dependant fields
	 *
	 * @return {SchoolDeliveryOptions}
	 */
	private get schoolDeliveryOptions(): SchoolDeliveryOptions {
		return getSchoolDeliveryOptions(this.studentDeliveryInfo, this.student.school);
	}

	/**
	 * Get the student's location from the group locations
	 *
	 * @return {GroupLocation | null}
	 */
	private get studentLocation(): GroupLocation | null {
		const locations = this.groupLocations || [];
		if (locations.length && this.student.restaurantId) {
			return locations.find(loc => loc.app8_restaurant === this.student.restaurantId) || null;
		}
		return null;
	}

	/**
	 * Display name of the student's location
	 *
	 * @return {string}
	 */
	private get studentLocationName(): string {
		return this.studentLocation?.name || this.restaurant.name;
	}

	/**
	 * Check if the student's location is the current school
	 *
	 * @return {boolean}
	 */
	private get isCurrentSchool(): boolean {
		return this.studentLocation?.app8_restaurant === this.restaurant.app8_restaurant;
	}

	/**
	 * Set schools in the selectors
	 * Validate that the student's values are still valid.
	 *
	 * @return {void}
	 */
	private created(): void {
		this.schools = this.studentDeliveryInfo.schools.map((school) => school.name);
		validateStudentFields({ student: this.student, restaurantDeliveryInfo: this.studentDeliveryInfo, schoolDeliveryOptions: this.schoolDeliveryOptions });
	}
}
